import { createContext, useContext, useEffect, useState, useCallback, use } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface StudyGuideByPassageDataContextType {
    studyGuideByPassage: StudyGuideByPassage[];
    studyGuideByPassageMap: Map<string, StudyGuideByPassage>;
    loadStudyGuideByPassageId: (passageId: string) => Promise<StudyGuideByPassage | undefined>;
}

export const StudyGuideByPassageDataContext = createContext<StudyGuideByPassageDataContextType | undefined>(undefined);

export function StudyGuideByPassageDataProvider({
    children,
    studyGuideByPassage: initialData
}: {
    children: React.ReactNode;
    studyGuideByPassage: StudyGuideByPassage[];
}) {
    const [loader] = useState(() => new LazyDataLoader<StudyGuideByPassage>(DBCollection.studyGuideByPassage, 'passageId'));
    const [cachedItems, setCachedItems] = useState<StudyGuideByPassage[]>([]);

    useEffect(() => {
        if (initialData && initialData.length > 0) {
            loader.preload(initialData);
            setCachedItems(loader.getAllCached());
        }
    }, [initialData, loader]);

    const studyGuideByPassageMap = useState(() => {
        const map = new Map<string, StudyGuideByPassage>();
        cachedItems.forEach(item => map.set(item.passageId, item));
        return map;
    })[0];

    // Update map when cached items change
    useEffect(() => {
        studyGuideByPassageMap.clear();
        cachedItems.forEach(item => studyGuideByPassageMap.set(item.passageId, item));
    }, [cachedItems, studyGuideByPassageMap]);

    const loadStudyGuideByPassageId = useCallback(async (passageId: string): Promise<StudyGuideByPassage | undefined> => {
        const item = await loader.get(passageId);
        if (item) {
            setCachedItems(loader.getAllCached());
        }
        return item;
    }, [loader]);

    return (
        <StudyGuideByPassageDataContext.Provider value={{
            studyGuideByPassage: cachedItems,
            studyGuideByPassageMap,
            loadStudyGuideByPassageId
        }}>
            {children}
        </StudyGuideByPassageDataContext.Provider>
    );
}

export function useStudyGuideByPassageData(passageId?: string) {
    const context = useContext(StudyGuideByPassageDataContext);
    if (context === undefined) {
        throw new Error('useStudyGuideByPassageData must be used within a StudyGuideByPassageDataProvider');
    }

    const [data, setData] = useState<StudyGuideByPassage | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!passageId) {
            return;
        }

        const cached = context.studyGuideByPassageMap.get(passageId);
        if (cached) {
            setData(cached);
            return;
        }

        setIsLoading(true);
        context.loadStudyGuideByPassageId(passageId)
            .then(loaded => {
                setData(loaded);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(`Failed to load study guide by passage for ${passageId}:`, err);
                setIsLoading(false);
            });
    }, [passageId, context]);

    return {
        data,
        isLoading,
        ...context
    };
}

